import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { CheckCircle, Mail, AlertTriangle, Loader2 } from 'lucide-react';
import { useAuth } from '../../components/self-service/auth/useAuth';
import { apiClient, ApiError } from '../../lib/apiClient';
import * as analytics from '../../lib/analytics';
import { EVENTS } from '../../lib/analytics';
import { usePublisherConfig } from '../../data/publisherConfigs';
import PrimaryButton from '../../components/self-service/shared/PrimaryButton';
import Banner from '../../components/self-service/shared/Banner';
import CtvBuyerAuthShell from '../../components/self-service/auth/CtvBuyerAuthShell';

interface InvitationView {
  advertiserId: string;
  advertiserName: string;
  email: string;
  status: 'pending' | 'accepted' | 'revoked';
  invitedAt: string;
  expiresAt: string | null;
  inviterEmail: string | null;
  /** Set by `getInvitationByToken` on the backend — the station the inviter
   *  most recently submitted from, used to pin the closed-loop redirect.
   *  `null` for fresh advertisers with no submissions yet. */
  defaultStationSlug?: string | null;
}

interface AcceptResponse {
  advertiserId?: string;
  defaultStationSlug?: string | null;
  ok?: false;
  needsSignIn?: true;
}

/**
 * Landing page for `/invitations/:token`. Handles **teammate invitations only**
 * after the seat redesign — the legacy "anonymous-submission self-claim" path
 * is gone (verify-email is now mandatory before submit, so there's no anon
 * submission to claim).
 *
 * Two paths through:
 *
 * 1. Invitee clicks the magic-link email → Supabase OTP exchange completes
 *    on this URL (`detectSessionInUrl: true`), `useAuth` reflects the new
 *    session, we POST /invitations/:token/accept, and redirect to /my-campaigns.
 *
 * 2. Invitee lands here directly (no session yet, e.g. clicked link in a
 *    different browser) → public GET fetches metadata, we show "you've been
 *    invited to <advertiser>", and trigger a magic link OTP back to this URL.
 */
const InvitationLandingPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { user, isAuthenticated, refreshMemberships } = useAuth();
  // Once the invite loads we know which station the inviter most recently
  // shipped from; bringing in that config lets the CTAs render with the
  // station accent instead of slate. Falls back to neutral when absent
  // (fresh advertiser with no submissions yet).
  const [stationSlug, setStationSlug] = React.useState<string | null>(null);
  const { config: stationConfig } = usePublisherConfig(stationSlug ?? undefined);
  const stationAccent = stationConfig?.accentColor || stationConfig?.primaryColor;
  const stationPrimary = stationConfig?.primaryColor;

  const [invite, setInvite] = React.useState<InvitationView | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [accepting, setAccepting] = React.useState(false);
  // Once we attempt accept (success OR error), this flips true and the
  // useEffect bails out forever. Prevents the catch path's setAccepting(false)
  // from re-triggering the effect and spamming the API.
  const acceptAttemptedRef = React.useRef(false);
  const [otpSent, setOtpSent] = React.useState(false);
  const [otpError, setOtpError] = React.useState<string | null>(null);

  // 1. Fetch the invitation metadata (public endpoint).
  React.useEffect(() => {
    if (!token) {
      setError('Missing invitation token.');
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const data = await apiClient.get<InvitationView>(`/invitations/${token}`, {
          anonymous: true,
        });
        if (!cancelled) {
          setInvite(data);
          if (data.defaultStationSlug) setStationSlug(data.defaultStationSlug);
        }
      } catch (e) {
        if (cancelled) return;
        const msg = e instanceof ApiError ? e.message : 'Failed to load invitation.';
        setError(msg);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  // 2. Once authenticated AND the invite is loaded, auto-accept exactly once
  //    per page lifetime. The acceptAttemptedRef guards against re-fire on
  //    every render — without it the catch path's `setAccepting(false)`
  //    would re-trigger the effect and spam the API in a tight loop.
  React.useEffect(() => {
    if (!token || !invite || !isAuthenticated || !user) return;
    if (invite.status !== 'pending') return;
    if (acceptAttemptedRef.current) return;
    acceptAttemptedRef.current = true;
    setAccepting(true);
    (async () => {
      try {
        const res = await apiClient.post<AcceptResponse>(
          `/invitations/${token}/accept`,
          { token },
        );
        if (res.ok === false && res.needsSignIn) {
          throw new Error(
            'Session not detected yet. Click the link in the invitation email again.',
          );
        }
        if (!res.advertiserId) {
          throw new Error('Invitation acceptance failed (no advertiser id returned).');
        }
        analytics.track(EVENTS.INVITATION_ACCEPTED, {
          station_slug: res.defaultStationSlug ?? null,
          advertiser_id: res.advertiserId,
        });
        // Refresh the cached memberships so the next page render reflects
        // the new advertiser link without waiting for a route-level fetch.
        await refreshMemberships().catch(() => {});
        // Closed-loop redirect: the backend tells us which station to drop
        // the user into. If it can't decide (advertiser has no submissions),
        // fall back to root — there's no global /my-campaigns to land on.
        const dest = res.defaultStationSlug
          ? `/${res.defaultStationSlug}/my-campaigns`
          : '/';
        navigate(dest, { replace: true });
      } catch (e) {
        setAccepting(false);
        const msg =
          e instanceof ApiError
            ? `${e.message} (status ${e.status})`
            : e instanceof Error
              ? e.message
              : 'Failed to accept invitation.';
        setError(msg);
      }
    })();
  }, [token, invite, isAuthenticated, user, navigate, refreshMemberships]);

  async function sendMagicLink() {
    if (!invite) return;
    setOtpError(null);
    setOtpSent(false);
    try {
      // Redirect back to this same landing page so the second-trip flow
      // (anon → click email → land here authenticated) closes the loop.
      // Backend (ADR 0001) mints the Supabase token + dispatches our
      // SendGrid `auth.signin` template — Supabase's built-in mailer is no
      // longer in the loop.
      await apiClient.post(
        '/auth/sign-in/request',
        {
          email: invite.email,
          redirectTo: `${window.location.origin}/invitations/${token}`,
        },
        { anonymous: true },
      );
      setOtpSent(true);
    } catch (e) {
      setOtpError(e instanceof Error ? e.message : 'Failed to send sign-in email.');
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center text-slate-500">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading invitation…
      </div>
    );
  }

  // CTV-751: the shell's CTVBuyer wordmark leads; station identity stays in
  // the copy and the accent-tinted CTAs (Seth: leading with the station logo
  // here was noisier, not clearer).
  if (error || !invite) {
    return (
      <CtvBuyerAuthShell variant="dark">
        <div className="bg-white rounded-[20px] shadow-2xl p-8 text-center">
            <AlertTriangle className="w-10 h-10 text-rose-500 mx-auto mb-4" />
            <h1 className="text-xl font-bold text-slate-900 mb-2">Invitation unavailable</h1>
            <p className="text-slate-600 text-sm mb-6">{error ?? 'This invitation could not be loaded.'}</p>
            <PrimaryButton
              type="button"
              onClick={() => navigate('/')}
              accentColor={stationAccent}
              primaryColor={stationPrimary}
            >
              Back to home
            </PrimaryButton>
        </div>
      </CtvBuyerAuthShell>
    );
  }

  if (invite.status === 'accepted') {
    return (
      <CtvBuyerAuthShell variant="dark">
          <div className="bg-white rounded-[20px] shadow-2xl p-8 text-center">
          <CheckCircle className="w-10 h-10 text-emerald-500 mx-auto mb-4" />
          <h1 className="text-xl font-bold text-slate-900 mb-2">Invitation already accepted</h1>
          <p className="text-slate-600 text-sm mb-6">
            You've already joined {invite.advertiserName}. Continue to your campaigns.
          </p>
          <PrimaryButton
            type="button"
            onClick={() =>
              navigate(
                invite.defaultStationSlug
                  ? `/${invite.defaultStationSlug}/my-campaigns`
                  : '/',
              )
            }
            accentColor={stationAccent}
            primaryColor={stationPrimary}
          >
            View my campaigns
          </PrimaryButton>
          </div>
      </CtvBuyerAuthShell>
    );
  }

  return (
    <CtvBuyerAuthShell variant="dark">
        <div className="bg-white rounded-[20px] shadow-2xl p-8">
        <h1 className="text-2xl font-bold text-slate-900 mb-1">You've been invited</h1>
        <p className="text-slate-600 text-sm mb-6">
          {invite.inviterEmail
            ? `${invite.inviterEmail} invited you to join `
            : 'You were invited to join '}
          <span className="font-semibold text-slate-900">{invite.advertiserName}</span>.
        </p>

        <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 mb-6 text-sm">
          <div className="flex items-center gap-2 text-slate-700">
            <Mail className="w-4 h-4 text-slate-500" />
            <span>Sign in as <span className="font-medium text-slate-900">{invite.email}</span> to accept.</span>
          </div>
        </div>

        {accepting ? (
          <div className="flex items-center gap-2 text-slate-600 text-sm">
            <Loader2 className="w-4 h-4 animate-spin" /> Accepting invitation…
          </div>
        ) : isAuthenticated && user?.email?.toLowerCase() !== invite.email.toLowerCase() ? (
          <Banner tone="caution" label="Different account">
            You're signed in as <span className="font-medium">{user?.email}</span>, but this
            invitation is for <span className="font-medium">{invite.email}</span>. Sign out and
            sign in with the invited address.
          </Banner>
        ) : (
          <PrimaryButton
            type="button"
            onClick={sendMagicLink}
            accentColor={stationAccent}
            primaryColor={stationPrimary}
            fullWidth
          >
            Send sign-in link to {invite.email}
          </PrimaryButton>
        )}

        {otpSent && (
          <div className="mt-4">
            <Banner tone="success" label="Check your inbox">
              Check your inbox at <span className="font-medium">{invite.email}</span> for a sign-in link.
              After signing in, you'll land back here to finish accepting.
            </Banner>
          </div>
        )}
        {otpError && (
          <div className="mt-4">
            <Banner tone="warn" label="Couldn't send">{otpError}</Banner>
          </div>
        )}

        {invite.expiresAt && (
          <p className="mt-6 text-xs text-slate-500">
            Invitation expires {new Date(invite.expiresAt).toLocaleString()}.
          </p>
        )}
        </div>
    </CtvBuyerAuthShell>
  );
};

export default InvitationLandingPage;
